// Regras de apresentação das telas de conta (`spec/tasks/WEB-61.md`, `spec/08` §4.1 e §4.3).
// Fora do JSX porque são as únicas partes testáveis sem render.

import type { VariantProps } from 'class-variance-authority';
import type { RateSettings, SellerProfile } from '../../api/queries/account.ts';
import type { badgeVariants } from '../../components/ui/badge.tsx';
import { formatCents, pctOf } from '../../lib/money.ts';

/** Tom do `Badge` do Zenith (WEB-100). Só o subconjunto que estas telas usam. */
export type StatusTone = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

/**
 * `bps` → percentual em pt-BR, por aritmética de inteiro. Sem `toFixed` e sem divisão em
 * float: `149 / 100` já é `1.49` com erro de ponto flutuante, e a mesma regra que vale
 * para dinheiro (`spec/17` §4.6) vale para a taxa que multiplica dinheiro.
 *
 * Continua aqui (e não no `pct` do kit, que escreve `1,49 %` com espaço) porque
 * `features/admin-rates` e o admin inteiro importam deste módulo: duas grafias de
 * percentual em telas vizinhas seria pior que o espaço a menos do croqui.
 */
export function formatBps(bps: number): string {
  const negative = bps < 0;
  const digits = String(Math.abs(Math.trunc(bps))).padStart(3, '0');
  const whole = Number(digits.slice(0, -2));
  const fraction = digits.slice(-2);
  return `${negative ? '−' : ''}${whole},${fraction}%`;
}

export const SELLER_STATUS: Record<SellerProfile['status'], { label: string; tone: StatusTone }> = {
  pending_kyc: { label: 'verificação pendente', tone: 'warning' },
  active: { label: 'ativa', tone: 'success' },
  suspended: { label: 'suspensa', tone: 'destructive' },
  closed: { label: 'encerrada', tone: 'secondary' },
};

export const KYC_STATUS: Record<SellerProfile['kyc_status'], { label: string; tone: StatusTone }> =
  {
    none: { label: 'não enviada', tone: 'secondary' },
    pending: { label: 'em análise', tone: 'warning' },
    approved: { label: 'aprovada', tone: 'success' },
    rejected: { label: 'reprovada', tone: 'destructive' },
  };

/** `null` é "sem limite", nunca `R$ 0,00`: a diferença é o saque inteiro passar ou não. */
/** Taxa de cartão e carteira internacionais: `4,50% por venda`, ou `indisponível` (conta sem taxa). */
export function intlFeeLabel(bps: number | null | undefined): string {
  return bps === null || bps === undefined ? 'indisponível' : `${formatBps(bps)} por venda`;
}

export function dailyLimitLabel(cents: number | null): string {
  return cents === null ? 'sem limite' : formatCents(cents);
}

// ---------------------------------------------------------------------------
// Taxas: rótulos, diferença entre versões e o simulador
// ---------------------------------------------------------------------------

/**
 * Campos que descrevem uma tabela. `reserve_pct_bps`/`reserve_days` ficam de fora: o PIX
 * não tem mais reserva (decisão de produto de 16/10/2026).
 */
export const RATE_KEYS = [
  'pix_pct_bps',
  'pix_fixed_cents',
  'withdraw_fee_cents',
  'daily_withdraw_limit_cents',
  'chargeback_fee_cents',
  'crypto_fee_pct_bps',
  'crypto_fee_fixed_usd_cents',
] as const;

export type RateKey = (typeof RATE_KEYS)[number];

/** Rótulo longo (drawer de comparação e `Notice` da mudança agendada). */
export const RATE_LABEL: Record<RateKey, string> = {
  pix_pct_bps: 'Pix · percentual',
  pix_fixed_cents: 'Pix · fixo',
  withdraw_fee_cents: 'Taxa de saque',
  daily_withdraw_limit_cents: 'Limite diário de saque',
  chargeback_fee_cents: 'Taxa de chargeback',
  crypto_fee_pct_bps: 'Cripto · percentual',
  crypto_fee_fixed_usd_cents: 'Cripto · fixo',
};

/** Rótulo curto (`Badge` da tabela de mudanças agendadas). */
export const RATE_SHORT: Record<RateKey, string> = {
  pix_pct_bps: 'Pix',
  pix_fixed_cents: 'Pix fixo',
  withdraw_fee_cents: 'Saque',
  daily_withdraw_limit_cents: 'Limite/dia',
  chargeback_fee_cents: 'Chargeback',
  crypto_fee_pct_bps: 'Cripto',
  crypto_fee_fixed_usd_cents: 'Cripto fixo',
};

/** Valor de um campo da tabela já formatado para leitura. */
export function formatRate(key: RateKey, rate: RateSettings): string {
  if (key === 'pix_pct_bps' || key === 'crypto_fee_pct_bps') {
    return formatBps(rate[key]);
  }
  if (key === 'crypto_fee_fixed_usd_cents') {
    // Dólar, não real: a taxa fixa de cripto é em centavos de dólar (`spec/33` §1).
    return `US$ ${formatCents(rate[key], { withSymbol: false })}`;
  }
  if (key === 'daily_withdraw_limit_cents') {
    return dailyLimitLabel(rate.daily_withdraw_limit_cents);
  }
  return formatCents(rate[key]);
}

/**
 * Campos em que duas versões diferem. Uma linha `inherits_default` é a marcadora de reset
 * (`spec/08` §4.3): os campos dela não valem nada, quem passa a valer é o default da
 * plataforma -, então comparar campo a campo mostraria diferença inventada.
 */
export function rateDiff(current: RateSettings, other: RateSettings): RateKey[] {
  if (other.inherits_default) {
    return [];
  }
  return RATE_KEYS.filter((key) => current[key] !== other[key]);
}

export interface Simulation {
  /** Parte percentual da taxa. */
  pctFeeCents: number;
  /** `pctOf(bruto) + fixo`, a fórmula de `computeFees` (`packages/core/src/transactions/fees.ts`). */
  feeCents: number;
  /** Bruto − taxa. */
  netCents: number;
  /** Custo efetivo em basis points, por inteiro (nada de float até o rótulo). */
  effectiveBps: number;
}

/** A mesma conta do servidor quando o Pix é confirmado, feita no cliente sobre a taxa vigente. */
export function simulate(amountCents: number, rate: RateSettings): Simulation {
  const pctFeeCents = pctOf(amountCents, rate.pix_pct_bps);
  const feeCents = pctFeeCents + rate.pix_fixed_cents;
  const netCents = amountCents - feeCents;
  return {
    pctFeeCents,
    feeCents,
    netCents,
    effectiveBps: amountCents === 0 ? 0 : Math.round((feeCents * 10_000) / amountCents),
  };
}
